import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DownloadOutlined, EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { orderService } from '../../services/orderService';
import {
  CLIENT_PLATFORM_MAP,
  Order,
  OrderQueryRequest,
  OrderStatus,
  ORDER_SOURCE_MAP,
  ORDER_STATUS_MAP,
  Page,
  PAYMENT_SCENE_MAP,
  YeepayRefundRecord,
} from '../../types';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { hasMenuPermission } from '../../utils/permissions';
import { displayValue, resolvePaymentChannelText, resolvePaymentProviderText } from '../../utils/orderDisplay';
import DealerSelector from '../../components/DealerSelector';
import BatchSelector from '../../components/BatchSelector';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const OrderList: React.FC = () => {
  const { user, menus, permissions } = useAuth();
  const canSyncYeepayOrder = hasMenuPermission(menus, permissions, 'YEEPAY_ORDER', 'SYNC');
  const canCreateYeepayRefund = hasMenuPermission(menus, permissions, 'YEEPAY_REFUND', 'CREATE');
  const canSyncYeepayRefund = hasMenuPermission(menus, permissions, 'YEEPAY_REFUND', 'SYNC');
  const canReadYeepayRefund = hasMenuPermission(menus, permissions, 'YEEPAY_REFUND', 'READ');
  const [refundForm] = Form.useForm();
  const [orderList, setOrderList] = useState<Order[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState<Partial<OrderQueryRequest>>({
    pageNo: 1,
    pageSize: 20,
  });
  const [orderNoInput, setOrderNoInput] = useState<string>('');
  const [deviceNumInput, setDeviceNumInput] = useState<string>('');
  const [orderStatusInput, setOrderStatusInput] = useState<OrderStatus | undefined>();
  const [dateRangeInput, setDateRangeInput] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [batchFilterValue, setBatchFilterValue] = useState<number | undefined>();
  const [selectedDealerId, setSelectedDealerId] = useState<number | undefined>(() =>
    user?.dealerType === 1 ? undefined : user?.dealerId,
  );
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundRecordsModalVisible, setRefundRecordsModalVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [refundRecords, setRefundRecords] = useState<YeepayRefundRecord[]>([]);
  const [loadingStates, setLoadingStates] = useState({
    table: false,
    export: false,
    refresh: false,
    search: false,
    operation: false,
    syncOrder: false,
    syncRefund: false,
    refundRecords: false,
  });
  const filtersRef = useRef<Partial<OrderQueryRequest>>({
    pageNo: 1,
    pageSize: 20,
  });

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const setLoading = useCallback((key: keyof typeof loadingStates, loading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [key]: loading,
    }));
  }, []);

  const withLoading = useCallback(
    <T extends unknown[]>(key: keyof typeof loadingStates, fn: (...args: T) => Promise<void>) =>
      async (...args: T) => {
        if (loadingStates[key]) {
          message.warning('操作进行中，请稍候...');
          return;
        }
        setLoading(key, true);
        try {
          await fn(...args);
        } finally {
          setLoading(key, false);
        }
      },
    [loadingStates, setLoading],
  );

  const isYeepayOrder = (record: Order) =>
    record.paymentChannel === 'YEEPAY' || record.paymentProvider === 'YEEPAY';

  const loadOrderList = useCallback(
    async (params?: Partial<OrderQueryRequest>) => {
      setLoading('table', true);
      try {
        const currentFilters = filtersRef.current;
        const queryParams: OrderQueryRequest = {
          ...currentFilters,
          ...params,
          pageNo: params?.pageNo || currentFilters.pageNo || 1,
          pageSize: params?.pageSize || currentFilters.pageSize || 20,
        };
        const response = await orderService.getOrderList(queryParams);
        if (response.status !== 1) {
          message.error(response.msg || '获取订单列表失败');
          return;
        }
        const data = response.data as Page<Order>;
        setOrderList(data.content || []);
        setPagination({
          current: data.number + 1,
          pageSize: data.size,
          total: data.totalElements,
        });
        setFilters(queryParams);
      } catch {
        message.error('获取订单列表失败');
      } finally {
        setLoading('table', false);
      }
    },
    [setLoading],
  );

  useEffect(() => {
    if (user && user.dealerType !== 1 && user.dealerId) {
      setSelectedDealerId(user.dealerId);
    }
  }, [user]);

  useEffect(() => {
    loadOrderList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = withLoading('search', async () => {
    const queryParams: Partial<OrderQueryRequest> = {
      pageNo: 1,
    };
    if (orderNoInput.trim()) {
      queryParams.orderNo = orderNoInput.trim();
    }
    if (deviceNumInput.trim()) {
      queryParams.deviceNum = deviceNumInput.trim();
    }
    if (orderStatusInput !== undefined) {
      queryParams.orderStatus = orderStatusInput;
    }
    if (dateRangeInput && dateRangeInput[0] && dateRangeInput[1]) {
      queryParams.startTime = dateRangeInput[0].valueOf();
      queryParams.endTime = dateRangeInput[1].valueOf();
    }
    if (selectedDealerId) {
      queryParams.dealerId = selectedDealerId;
    }
    if (batchFilterValue) {
      queryParams.deviceBatchId = batchFilterValue;
    }
    await loadOrderList(queryParams);
  });

  const handleRefresh = withLoading('refresh', async () => {
    await loadOrderList({ pageNo: pagination.current, pageSize: pagination.pageSize });
  });

  const handleSyncYeepayOrder = withLoading('syncOrder', async (record: Order) => {
    const response = await orderService.syncYeepayOrderStatus(record.orderNo);
    if (response.status !== 1) {
      message.error(response.msg || '同步易宝订单状态失败');
      return;
    }
    message.success('易宝订单状态已同步');
    await loadOrderList({ pageNo: pagination.current, pageSize: pagination.pageSize });
  });

  const handleOpenRefundModal = (record: Order) => {
    setCurrentOrder(record);
    refundForm.setFieldsValue({
      refundAmount: record.actualAmount,
      reason: '',
    });
    setRefundModalVisible(true);
  };

  const handleSubmitRefund = async () => {
    if (!currentOrder) {
      return;
    }
    const values = await refundForm.validateFields();
    if (values.refundAmount <= 0 || values.refundAmount > currentOrder.actualAmount) {
      message.error('退款金额必须大于0且不超过订单实付金额');
      return;
    }
    setLoading('operation', true);
    try {
      const response = await orderService.createYeepayRefund(currentOrder.orderNo, {
        refundAmount: values.refundAmount,
        reason: values.reason,
      });
      if (response.status !== 1) {
        message.error(response.msg || '发起退款失败');
        return;
      }
      message.success('退款申请已提交');
      setRefundModalVisible(false);
      await loadOrderList({ pageNo: pagination.current, pageSize: pagination.pageSize });
    } catch {
      message.error('发起退款失败');
    } finally {
      setLoading('operation', false);
    }
  };

  const fetchRefundRecords = useCallback(
    async (order: Order) => {
      setLoading('refundRecords', true);
      try {
        const response = await orderService.queryRefundRecords({
          orderId: order.id,
          orderNo: order.orderNo,
        });
        if (response.status !== 1) {
          message.error(response.msg || '查询退款记录失败');
          return;
        }
        setRefundRecords(response.data || []);
      } catch {
        message.error('查询退款记录失败');
      } finally {
        setLoading('refundRecords', false);
      }
    },
    [setLoading],
  );

  const handleOpenRefundRecords = async (record: Order) => {
    setCurrentOrder(record);
    setRefundRecordsModalVisible(true);
    await fetchRefundRecords(record);
  };

  const handleSyncRefundStatus = withLoading('syncRefund', async (record: YeepayRefundRecord) => {
    const response = await orderService.syncRefundStatus(record.refundRequestId);
    if (response.status !== 1) {
      message.error(response.msg || '同步退款状态失败');
      return;
    }
    message.success('退款状态已同步');
    if (currentOrder) {
      await fetchRefundRecords(currentOrder);
    }
  });

  const handleExport = withLoading('export', async () => {
    try {
      const blob = await orderService.exportOrders(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `订单数据_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('导出成功');
    } catch {
      message.error('导出失败');
    }
  });

  const handleViewDetail = (record: Order) => {
    Modal.info({
      title: '订单详情',
      width: 760,
      content: (
        <div style={{ maxHeight: 620, overflowY: 'auto' }}>
          <div style={{ marginBottom: 12 }}>
            <h4>订单基本信息</h4>
            <p><strong>订单号：</strong>{record.orderNo}</p>
            <p><strong>订单状态：</strong>{record.orderStatusText || ORDER_STATUS_MAP[record.orderStatus]?.text}</p>
            <p><strong>订单来源：</strong>{record.orderSourceText || ORDER_SOURCE_MAP[record.orderSource]}</p>
            <p><strong>创建时间：</strong>{dayjs(record.createTime).format('YYYY-MM-DD HH:mm:ss')}</p>
            <p><strong>支付时间：</strong>{record.payTime ? dayjs(record.payTime).format('YYYY-MM-DD HH:mm:ss') : displayValue()}</p>
          </div>
          <div style={{ marginBottom: 12 }}>
            <h4>支付路由信息</h4>
            <p><strong>支付渠道：</strong>{resolvePaymentChannelText(record)}</p>
            <p><strong>收单服务商：</strong>{resolvePaymentProviderText(record)}</p>
            <p><strong>实际支付方式：</strong>{displayValue(record.paymentMethodText)}</p>
            <p><strong>支付场景：</strong>{displayValue(record.paymentSceneText || (record.paymentScene ? PAYMENT_SCENE_MAP[record.paymentScene] : undefined))}</p>
            <p><strong>客户端平台：</strong>{record.clientPlatform ? CLIENT_PLATFORM_MAP[record.clientPlatform] : displayValue()}</p>
            <p><strong>客户端 AppID：</strong>{displayValue(record.clientAppId)}</p>
            <p><strong>商户号：</strong>{displayValue(record.merchantNo || record.merchantNoMasked)}</p>
            <p><strong>商户名称：</strong>{displayValue(record.merchantDisplayName || record.merchantConfigName)}</p>
            <p><strong>应用资质配置 ID：</strong>{displayValue(record.appCredentialId?.toString())}</p>
            <p><strong>商户配置 ID：</strong>{displayValue(record.merchantConfigId?.toString())}</p>
            <p><strong>第三方交易号：</strong>{displayValue(record.thirdTradeNo || record.transactionNo)}</p>
            <p><strong>易宝订单号：</strong>{displayValue(record.yeepayUniqueOrderNo)}</p>
            <p><strong>易宝银行流水：</strong>{displayValue(record.yeepayBankOrderId)}</p>
          </div>
          <div>
            <h4>充值上下文信息</h4>
            <p><strong>受让人ID：</strong>{displayValue(record.recipientThirdUserId)}</p>
            <p><strong>受让人快照：</strong>{displayValue(record.recipientSnapshot)}</p>
            <p><strong>充值设备号：</strong>{displayValue(record.rechargeDeviceNum || record.deviceNum)}</p>
            <p><strong>设备厂商：</strong>{displayValue(record.rechargeFirm || record.deviceFirm)}</p>
            <p><strong>充值来源：</strong>{displayValue(record.rechargeSource)}</p>
            <p><strong>充值会话ID：</strong>{displayValue(record.rechargeSessionId)}</p>
            <p><strong>操作者平台用户ID：</strong>{displayValue(record.actorPlatformUserId)}</p>
            <p><strong>付款人平台用户ID：</strong>{displayValue(record.payerPlatformUserId)}</p>
          </div>
        </div>
      ),
    });
  };

  const columns = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 200,
      render: (text: string, record: Order) => (
        <div>
          <div>{text}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.thirdTradeNo || record.transactionNo || '-'}</div>
        </div>
      ),
    },
    {
      title: '设备编号',
      key: 'deviceInfo',
      width: 180,
      render: (record: Order) => (
        <div>
          <div>{record.deviceNum || `设备${record.deviceId}`}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.deviceTypeName || '-'}</div>
        </div>
      ),
    },
    {
      title: '设备批次',
      key: 'deviceBatchName',
      width: 150,
      render: (record: Order) => displayValue(record.deviceBatchName),
    },
    {
      title: '套餐信息',
      key: 'packageInfo',
      width: 220,
      render: (record: Order) => {
        let packageDisplayName = record.packageName || `套餐${record.packageId}`;
        try {
          if (record.remark) {
            const remarkData = JSON.parse(record.remark);
            if (remarkData.packageGroupName && remarkData.packageName) {
              packageDisplayName = `${remarkData.packageGroupName} / ${remarkData.packageName}`;
            }
          }
        } catch {
          // remark 非 JSON 时沿用 packageName
        }
        return (
          <div>
            <div>{packageDisplayName}</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              ¥{record.packageActualPrice?.toFixed(2) || record.actualAmount?.toFixed(2) || '0.00'}
            </div>
          </div>
        );
      },
    },
    {
      title: '支付渠道',
      key: 'paymentChannelText',
      width: 150,
      render: (_: unknown, record: Order) => resolvePaymentChannelText(record),
    },
    {
      title: '收单服务商',
      key: 'paymentProviderText',
      width: 130,
      render: (_: unknown, record: Order) => resolvePaymentProviderText(record),
    },
    {
      title: '商户号',
      key: 'merchantNo',
      width: 160,
      render: (_: unknown, record: Order) => displayValue(record.merchantNo || record.merchantNoMasked),
    },
    {
      title: 'AppID',
      key: 'clientAppId',
      width: 180,
      render: (_: unknown, record: Order) => displayValue(record.clientAppId),
    },
    {
      title: '金额',
      key: 'amount',
      width: 120,
      render: (record: Order) => (
        <div>
          <div>¥{record.actualAmount?.toFixed(2) || '0.00'}</div>
          {record.originalAmount !== record.actualAmount && (
            <div style={{ fontSize: 12, color: '#999', textDecoration: 'line-through' }}>
              ¥{record.originalAmount?.toFixed(2) || '0.00'}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '订单状态',
      dataIndex: 'orderStatusText',
      key: 'orderStatusText',
      width: 110,
      render: (text: string, record: Order) => {
        const statusInfo = ORDER_STATUS_MAP[record.orderStatus];
        return <Tag color={statusInfo?.color}>{text || statusInfo?.text}</Tag>;
      },
    },
    {
      title: '经销商',
      key: 'dealerInfo',
      width: 140,
      render: (record: Order) => (
        <div>
          <div>{record.dealerName || `经销商${record.dealerId}`}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.dealerCode || '-'}</div>
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 170,
      render: (time: string) => (time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '支付时间',
      dataIndex: 'payTime',
      key: 'payTime',
      width: 170,
      render: (time: string) => (time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right' as const,
      render: (record: Order) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
            查看
          </Button>
          {canSyncYeepayOrder && isYeepayOrder(record) && (
            <Button type="link" size="small" onClick={() => handleSyncYeepayOrder(record)} loading={loadingStates.syncOrder}>
              同步易宝状态
            </Button>
          )}
          {canCreateYeepayRefund && isYeepayOrder(record) && Number(record.orderStatus) === 1 && (
            <Button type="link" size="small" onClick={() => handleOpenRefundModal(record)}>
              发起退款
            </Button>
          )}
          {canReadYeepayRefund && isYeepayOrder(record) && (
            <Button type="link" size="small" onClick={() => handleOpenRefundRecords(record)}>
              退款记录
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>订单列表</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loadingStates.refresh}>
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <Space wrap>
              <Input
                placeholder="订单号"
                allowClear
                style={{ width: 200 }}
                value={orderNoInput}
                onChange={e => setOrderNoInput(e.target.value)}
                onPressEnter={() => handleSearch()}
              />
              <Input
                placeholder="设备号"
                allowClear
                style={{ width: 200 }}
                value={deviceNumInput}
                onChange={e => setDeviceNumInput(e.target.value)}
                onPressEnter={() => handleSearch()}
              />
              <Select
                placeholder="订单状态"
                style={{ width: 130 }}
                allowClear
                value={orderStatusInput}
                onChange={value => setOrderStatusInput(value)}
              >
                <Option value={0}>待支付</Option>
                <Option value={1}>已支付</Option>
                <Option value={2}>已取消</Option>
                <Option value={3}>已退款</Option>
                <Option value={4}>部分退款</Option>
              </Select>
              {user?.dealerType === 1 && (
                <DealerSelector
                  placeholder="筛选经销商"
                  style={{ width: 220 }}
                  allowClear
                  value={selectedDealerId}
                  onChange={(dealerId: number) => {
                    setSelectedDealerId(dealerId);
                    if (batchFilterValue) {
                      setBatchFilterValue(undefined);
                    }
                  }}
                  status={1}
                />
              )}
              <BatchSelector
                placeholder="筛选批次"
                style={{ width: 300 }}
                allowClear
                value={batchFilterValue}
                onChange={(batchId: number) => setBatchFilterValue(batchId)}
                dealerId={selectedDealerId}
              />
              <RangePicker
                placeholder={['开始日期', '结束日期']}
                value={dateRangeInput}
                onChange={dates => setDateRangeInput(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              />
              <Button type="primary" icon={<SearchOutlined />} onClick={() => handleSearch()} loading={loadingStates.search}>
                查询
              </Button>
            </Space>
          </Col>
          <Col>
            <Button icon={<DownloadOutlined />} onClick={handleExport} loading={loadingStates.export}>
              导出数据
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={orderList}
          loading={loadingStates.table}
          rowKey="id"
          scroll={{ x: 2400 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
            onChange: (page, pageSize) => {
              loadOrderList({
                pageNo: page,
                pageSize,
              });
            },
          }}
        />
      </Card>

      <Modal
        title={`发起易宝退款${currentOrder ? ` - ${currentOrder.orderNo}` : ''}`}
        open={refundModalVisible}
        onCancel={() => setRefundModalVisible(false)}
        onOk={handleSubmitRefund}
        confirmLoading={loadingStates.operation}
      >
        <Form form={refundForm} layout="vertical">
          <Form.Item label="退款金额" name="refundAmount" rules={[{ required: true, message: '请输入退款金额' }]}>
            <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="退款原因"
            name="reason"
            rules={[{ required: true, message: '请输入退款原因' }, { max: 100, message: '退款原因最多100字符' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`退款记录${currentOrder ? ` - ${currentOrder.orderNo}` : ''}`}
        open={refundRecordsModalVisible}
        width={900}
        footer={null}
        onCancel={() => setRefundRecordsModalVisible(false)}
      >
        <Table
          rowKey="id"
          loading={loadingStates.refundRecords}
          dataSource={refundRecords}
          pagination={false}
          columns={[
            { title: '退款请求号', dataIndex: 'refundRequestId', key: 'refundRequestId', width: 180 },
            { title: '退款金额', dataIndex: 'refundAmount', key: 'refundAmount', width: 110, render: (v: number) => `¥${v?.toFixed(2)}` },
            { title: '退款状态', dataIndex: 'refundStatus', key: 'refundStatus', width: 120 },
            { title: '易宝退款单号', dataIndex: 'yeepayUniqueRefundNo', key: 'yeepayUniqueRefundNo', width: 180, render: (v: string) => v || '-' },
            {
              title: '失败原因',
              key: 'failureReason',
              render: (_: unknown, record: YeepayRefundRecord) => record.failureReason || record.failReason || '-',
            },
            {
              title: '创建时间',
              key: 'createdAt',
              width: 170,
              render: (_: unknown, record: YeepayRefundRecord) => record.createdAt || record.createTime || '-',
            },
            {
              title: '操作',
              key: 'actions',
              width: 100,
              render: (_: unknown, record: YeepayRefundRecord) => (
                <Button
                  type="link"
                  size="small"
                  disabled={!canSyncYeepayRefund}
                  loading={loadingStates.syncRefund}
                  onClick={() => handleSyncRefundStatus(record)}
                >
                  同步状态
                </Button>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default OrderList; 